import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import {
  ChevronLeft, ChevronRight, CalendarOff, Loader2, AlertCircle,
} from 'lucide-react';
import { Card } from '../../ui-v2';
import { cn } from '../../../../lib/cn';
import PanelFranjasDelDia from '../calendario/PanelFranjasDelDia';
import BloquearFranjaModal from '../calendario/BloquearFranjaModal';
import BloquearBulkModal from '../calendario/BloquearBulkModal';
import {
  getTecnicoCampanas,
  getTecnicoCampana,
  getTecnicoCampanaCalendario,
  getBloqueosTecnico,
  crearBloqueosBatch,
  eliminarBloqueoTecnico,
} from '../api';
// Contexto de usuario del proyecto (mismo que usa el admin pages/Calendario.jsx).
import { UsuarioContext } from '../../../providers/personaContext';

/**
 * TabCalendario · Panel Técnico · reescritura Fase 12f.
 *
 * Espeja el patrón de pages/Calendario.jsx (admin) pero:
 *   - Fuente de datos: /tecnico/campanas[...] (self-scoped por JWT).
 *   - esAdmin={false} en modales.
 *   - Selector de campaña (de mis asignadas) en lugar de filtro admin.
 *   - Sin selector de técnico (se deriva del usuario logueado).
 *
 * Reutiliza sin cambios:
 *   - PanelFranjasDelDia v3
 *   - BloquearFranjaModal
 *   - BloquearBulkModal
 *   - crearBloqueosBatch (backend batch de 12d, self-scoped cuando esAdmin=false)
 *   - eliminarBloqueoTecnico
 */

const DOW_CORTO = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// ─── Helpers de fecha ───

function pad(n) { return String(n).padStart(2, '0'); }

function primerDiaMes({ year, month }) {
  return `${year}-${pad(month)}-01`;
}
function ultimoDiaMes({ year, month }) {
  const d = new Date(year, month, 0);   // día 0 del mes siguiente = último del actual
  return `${year}-${pad(month)}-${pad(d.getDate())}`;
}
function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Construye la matriz del mes para renderizar el grid.
 * Devuelve 6 semanas × 7 días (algunas con días de mes anterior/siguiente).
 * Cada día: { fecha: "YYYY-MM-DD", diaMes, esDelMes, esHoy }.
 */
function matrizDelMes({ year, month }) {
  const primerDia = new Date(year, month - 1, 1);
  const diaSemanaLun0 = (primerDia.getDay() + 6) % 7;   // Lunes=0..Domingo=6
  const inicio = new Date(year, month - 1, 1 - diaSemanaLun0);
  const hoy = hoyISO();

  const semanas = [];
  const cur = new Date(inicio);
  for (let s = 0; s < 6; s++) {
    const semana = [];
    for (let d = 0; d < 7; d++) {
      const y = cur.getFullYear();
      const m = cur.getMonth() + 1;
      const dm = cur.getDate();
      const fecha = `${y}-${pad(m)}-${pad(dm)}`;
      semana.push({
        fecha,
        diaMes: dm,
        esDelMes: (m === month && y === year),
        esHoy: fecha === hoy,
      });
      cur.setDate(cur.getDate() + 1);
    }
    semanas.push(semana);
  }
  return semanas;
}

// ────────────────────────────────────────────────────────────────
//  Componente principal
// ────────────────────────────────────────────────────────────────

export default function TabCalendario() {

  const { persona } = useContext(UsuarioContext);
  const usuarioId = persona?.id ?? persona?.Id;

  // Estado del filtro/navegación
  const [campanas, setCampanas] = useState([]);
  const [campanaId, setCampanaId] = useState(null);
  const [campanaSel, setCampanaSel] = useState(null);   // detalle + franjas
  const [mesRef, setMesRef] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [diaSel, setDiaSel] = useState(null);

  // Datos del mes actual (de la campaña filtrada)
  const [citas, setCitas] = useState([]);
  const [bloqueos, setBloqueos] = useState([]);

  const [loadingCampanas, setLoadingCampanas] = useState(true);
  const [loadingMes, setLoadingMes] = useState(false);
  const [error, setError] = useState(null);

  // Modales
  const [modalFranja, setModalFranja] = useState({ open: false });
  const [modalBulk, setModalBulk] = useState({ open: false });

  // ─── Cargar mis campañas al mount ───
  useEffect(() => {
    let cancelado = false;
    (async () => {
      setLoadingCampanas(true); setError(null);
      try {
        const data = await getTecnicoCampanas();
        if (cancelado) return;
        setCampanas(data);
        if (data.length > 0) setCampanaId((prev) => prev ?? data[0].id);
      } catch (e) {
        if (!cancelado) setError('No se pudieron cargar tus campañas.');
      } finally {
        if (!cancelado) setLoadingCampanas(false);
      }
    })();
    return () => { cancelado = true; };
  }, []);

  // ─── Cargar detalle campaña al cambiar campanaId ───
  useEffect(() => {
    if (!campanaId) { setCampanaSel(null); return; }
    let cancelado = false;
    (async () => {
      try {
        const data = await getTecnicoCampana(campanaId);
        if (!cancelado) setCampanaSel(data);
      } catch (e) {
        if (!cancelado) setCampanaSel(null);
      }
    })();
    return () => { cancelado = true; };
  }, [campanaId]);

  // ─── Cargar citas + bloqueos del mes ───
  const recargarMes = useCallback(async () => {
    if (!campanaId || !usuarioId) return;
    setLoadingMes(true);
    try {
      const desde = primerDiaMes(mesRef);
      const hasta = ultimoDiaMes(mesRef);
      const [cs, bs] = await Promise.all([
        getTecnicoCampanaCalendario(campanaId, { desde, hasta }),
        getBloqueosTecnico(usuarioId, { desde, hasta }),
      ]);
      setCitas(cs || []);
      setBloqueos(bs || []);
    } catch (e) {
      console.error('[TabCalendario] cargar mes', e);
      setError(e?.response?.data?.error || 'No se pudo cargar el calendario del mes.');
    } finally {
      setLoadingMes(false);
    }
  }, [campanaId, mesRef, usuarioId]);

  useEffect(() => { recargarMes(); }, [recargarMes]);

  // ─── Al cambiar campaña o mes, limpiar día seleccionado ───
  useEffect(() => {
    setDiaSel(null);
  }, [campanaId, mesRef]);

  // ─── Datos derivados del día seleccionado ───
  const citasDelDia = useMemo(() => {
    if (!diaSel) return [];
    return citas.filter((c) => c.fecha === diaSel).map((c) => ({
      ...c,
      persona_nombre: c.usuario?.nombre_completo || null,
    }));
  }, [citas, diaSel]);

  const bloqueosDelDia = useMemo(() => {
    if (!diaSel) return [];
    return bloqueos.filter((b) => b.fecha === diaSel);
  }, [bloqueos, diaSel]);

  // Contadores por día para el grid
  const contadoresPorDia = useMemo(() => {
    const map = new Map();
    for (const c of citas) {
      if (!map.has(c.fecha)) map.set(c.fecha, { citas: 0, bloqueos: 0 });
      map.get(c.fecha).citas++;
    }
    for (const b of bloqueos) {
      if (!map.has(b.fecha)) map.set(b.fecha, { citas: 0, bloqueos: 0 });
      map.get(b.fecha).bloqueos++;
    }
    return map;
  }, [citas, bloqueos]);

  // ─── Handlers de modales ───

  function handleBloquearFranja({ franja, fecha }) {
    setModalFranja({
      open: true, modo: 'franja', fecha,
      horaInicio: franja.hora_inicio, horaFin: franja.hora_fin,
    });
  }
  function handleBloquearSlot({ franja, slot, fecha }) {
    setModalFranja({
      open: true, modo: 'slot', fecha,
      horaInicio: slot.hora_inicio, horaFin: slot.hora_fin,
    });
  }
  function handleBloquearDia({ franjas, fecha }) {
    setModalBulk({
      open: true,
      titulo: 'Bloquear día completo',
      rangos: franjas,
      fecha,
    });
  }
  function handleBloquearSeleccion({ slots, fecha }) {
    setModalBulk({
      open: true,
      titulo: `Bloquear ${slots.length} ${slots.length === 1 ? 'slot' : 'slots'}`,
      rangos: slots.map((s) => ({ hora_inicio: s.hora_inicio, hora_fin: s.hora_fin })),
      fecha,
    });
  }

  async function handleGuardarBloqueos(bloqueosArr) {
    await crearBloqueosBatch(bloqueosArr);
    await recargarMes();
  }

  async function handleDesbloquear(bloqueo) {
    if (!window.confirm('¿Desbloquear esta franja/slot?')) return;
    try {
      await eliminarBloqueoTecnico(bloqueo.id);
      setBloqueos((prev) => prev.filter((b) => b.id !== bloqueo.id));
    } catch (e) {
      console.error('[TabCalendario] desbloquear', e);
      alert(e?.response?.data?.error || 'No se pudo desbloquear.');
    }
  }

  // ─── Navegación mes ───
  function navegarMes(delta) {
    setMesRef((m) => {
      let mm = m.month + delta;
      let yy = m.year;
      if (mm < 1) { mm = 12; yy--; }
      if (mm > 12) { mm = 1; yy++; }
      return { year: yy, month: mm };
    });
  }

  // ────────────────────────────────────────────────────────────
  //  Render
  // ────────────────────────────────────────────────────────────

  if (loadingCampanas) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        <span className="text-sm">Cargando tus campañas...</span>
      </div>
    );
  }

  if (campanas.length === 0) {
    return (
      <Card className="p-8 text-center">
        <CalendarOff className="w-8 h-8 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">
          No estás asignado como técnico a ninguna campaña activa.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">

      {/* Header: selector campaña + navegación mes */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-slate-700">Campaña:</label>
          <select
            value={campanaId || ''}
            onChange={(e) => setCampanaId(parseInt(e.target.value, 10))}
            className="border border-slate-200 rounded-md px-3 py-1.5 text-sm min-w-[200px]"
          >
            {campanas.map((c) => (
              <option key={c.id} value={c.id}>
                {c.titulo}{c.estado === 'Pausada' ? ' · Pausada' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navegarMes(-1)}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
            aria-label="Mes anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-slate-900 tabular-nums min-w-[130px] text-center">
            {MESES[mesRef.month - 1]} {mesRef.year}
          </span>
          <button
            type="button"
            onClick={() => navegarMes(1)}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
            aria-label="Mes siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Grid mensual + panel lateral */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        <GridMensual
          mesRef={mesRef}
          diaSel={diaSel}
          onSeleccionar={setDiaSel}
          contadoresPorDia={contadoresPorDia}
          loading={loadingMes}
        />

        <PanelFranjasDelDia
          campanaId={campanaId}
          fechaSeleccionada={diaSel}
          duracionCita={campanaSel?.duracion_cita}
          franjasPorDia={campanaSel?.franjas}
          citasDelDia={citasDelDia}
          bloqueosDelDia={bloqueosDelDia}
          onBloquearFranja={handleBloquearFranja}
          onBloquearSlot={handleBloquearSlot}
          onBloquearDia={handleBloquearDia}
          onBloquearSeleccion={handleBloquearSeleccion}
          onDesbloquear={handleDesbloquear}
        />
      </div>

      {/* Modales */}
      {modalFranja.open && (
        <BloquearFranjaModal
          open={modalFranja.open}
          onClose={() => setModalFranja({ open: false })}
          modo={modalFranja.modo}
          fecha={modalFranja.fecha}
          horaInicio={modalFranja.horaInicio}
          horaFin={modalFranja.horaFin}
          esAdmin={false}
          tecnicoIdActual={usuarioId}
          fechaLimiteCampana={campanaSel?.fecha_limite?.slice(0, 10)}
          onSave={handleGuardarBloqueos}
        />
      )}

      {modalBulk.open && (
        <BloquearBulkModal
          open={modalBulk.open}
          onClose={() => setModalBulk({ open: false })}
          titulo={modalBulk.titulo}
          fecha={modalBulk.fecha}
          rangos={modalBulk.rangos}
          esAdmin={false}
          tecnicoIdActual={usuarioId}
          fechaLimiteCampana={campanaSel?.fecha_limite?.slice(0, 10)}
          onSave={handleGuardarBloqueos}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  Sub-componente: GridMensual
// ────────────────────────────────────────────────────────────────

function GridMensual({ mesRef, diaSel, onSeleccionar, contadoresPorDia, loading }) {
  const semanas = useMemo(() => matrizDelMes(mesRef), [mesRef]);

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden relative">
      {loading && (
        <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        </div>
      )}

      {/* Cabecera días semana */}
      <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
        {DOW_CORTO.map((d) => (
          <div
            key={d}
            className="px-2 py-2 text-center text-[11px] font-medium text-slate-500 uppercase tracking-wider"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Semanas */}
      <div className="divide-y divide-slate-100">
        {semanas.map((semana, i) => (
          <div key={i} className="grid grid-cols-7 divide-x divide-slate-100">
            {semana.map((dia) => (
              <CeldaDia
                key={dia.fecha}
                dia={dia}
                sel={diaSel === dia.fecha}
                contadores={contadoresPorDia.get(dia.fecha)}
                onClick={() => onSeleccionar(dia.fecha)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function CeldaDia({ dia, sel, contadores, onClick }) {
  const { diaMes, esDelMes, esHoy } = dia;
  const nCitas = contadores?.citas || 0;
  const nBloqueos = contadores?.bloqueos || 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "min-h-[70px] text-left p-1.5 transition-colors relative",
        "hover:bg-slate-50",
        !esDelMes && "opacity-30",
        sel && "bg-primary/5 ring-1 ring-primary ring-inset",
      )}
    >
      <div className={cn(
        "text-xs tabular-nums mb-1",
        esHoy && "font-bold text-primary",
        !esHoy && esDelMes && "text-slate-700",
        !esDelMes && "text-slate-400"
      )}>
        {diaMes}
        {esHoy && (
          <span className="ml-1 text-[9px] font-normal">HOY</span>
        )}
      </div>

      {esDelMes && (nCitas > 0 || nBloqueos > 0) && (
        <div className="flex flex-col gap-0.5 text-[10px]">
          {nCitas > 0 && (
            <span className="inline-flex items-center gap-1 text-emerald-700 tabular-nums">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {nCitas} cita{nCitas > 1 ? 's' : ''}
            </span>
          )}
          {nBloqueos > 0 && (
            <span className="inline-flex items-center gap-1 text-slate-500 tabular-nums">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
              {nBloqueos} bloq.
            </span>
          )}
        </div>
      )}
    </button>
  );
}
